import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Lock, TrendingUp, FileText, Send, Eye, Upload } from "lucide-react";

// ─────────────────────────────────────────────────────────────
// Global → Increase tab
//   Super Admin opens a cycle for a fiscal year, sees each user's
//   final Q4/average score + auto-calculated increase %, and can
//   override either the score OR the percentage per row. Once
//   approved, the letters go out (email sending shipped next pass).
// ─────────────────────────────────────────────────────────────
export default function IncreasePage() {
  const [cycles, setCycles] = useState([]);
  const [active, setActive] = useState(null); // full cycle doc
  const [loading, setLoading] = useState(false);
  const [showOpener, setShowOpener] = useState(false);

  const loadCycles = async () => {
    try {
      const { data } = await api.get("/increase/cycles");
      setCycles(data);
      if (!active && data.length > 0) loadCycle(data[0].id);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadCycle = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/increase/cycles/${id}`);
      setActive(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadCycles(); /* eslint-disable-next-line */ }, []);

  const openCycle = async ({ fiscal_year, cycle_end }) => {
    try {
      const { data } = await api.post("/increase/cycles", { fiscal_year, cycle_end });
      toast.success(`Opened increase cycle for ${fiscal_year} — ${data.rows.length} rows`);
      setShowOpener(false);
      await loadCycles();
      loadCycle(data.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const approve = async () => {
    if (!active) return;
    if (!confirm(`Approve the ${active.fiscal_year} increase cycle? This locks the rates and will trigger letter emails to every employee once template + email are wired.`)) return;
    try {
      await api.post(`/increase/cycles/${active.id}/approve`);
      toast.success("Cycle approved — emails will send once template + Graph Mail.Send are wired");
      loadCycle(active.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async () => {
    if (!active) return;
    if (!confirm(`Delete the ${active.fiscal_year} cycle? This cannot be undone.`)) return;
    try {
      await api.delete(`/increase/cycles/${active.id}`);
      toast.success("Cycle deleted");
      setActive(null);
      loadCycles();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const overrideRow = async (row, updates) => {
    try {
      const { data } = await api.put(`/increase/cycles/${active.id}/rows/${row.user_id}`, updates);
      // Merge back
      const next = { ...active, rows: active.rows.map((r) => r.user_id === row.user_id ? data : r) };
      setActive(next);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="increase-page">
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Increase cycles</div>
          <div className="text-xs text-zinc-500 mt-1">
            Applies the schedule defined in Settings → Increase configuration. Super Admin can override any row.
          </div>
        </div>
        <div className="flex gap-2">
          {cycles.length > 0 && (
            <select data-testid="increase-cycle-picker"
              value={active?.id || ""} onChange={(e) => loadCycle(e.target.value)}
              className="border border-zinc-300 rounded-sm px-3 py-1.5 text-sm bg-white">
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>{c.fiscal_year} · {c.status} · {c.rows_count} rows</option>
              ))}
            </select>
          )}
          <button data-testid="open-cycle-btn" onClick={() => setShowOpener(true)}
            className="text-sm bg-emerald-600 text-white px-3 py-1.5 rounded-sm hover:bg-emerald-700 flex items-center gap-1">
            <Plus className="w-4 h-4" /> Open new cycle
          </button>
        </div>
      </div>

      {showOpener && <CycleOpener onCancel={() => setShowOpener(false)} onCreate={openCycle} />}

      {/* Template manager — always visible so admins can prep letters before Q4 completes */}
      <TemplateManager />

      {loading && <div className="text-sm text-zinc-500 py-6">Loading cycle…</div>}
      {!active && !loading && cycles.length === 0 && (
        <div className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-10 text-center">
          No cycles yet. Click <b>Open new cycle</b> to build the first one from your Q1–Q4 completed reviews.
        </div>
      )}

      {active && !loading && <CycleTable cycle={active} onOverride={overrideRow} onApprove={approve} onDelete={remove} onReload={() => loadCycle(active.id)} />}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// Template manager — upload / list / delete increase letter templates.
// ─────────────────────────────────────────────────────────────
const AVAILABLE_TOKENS = [
  "employee_name", "employee_email", "business_unit", "manager_name",
  "score", "percentage", "effective_date", "fiscal_year",
];

function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState("");
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/increase/templates"); setTemplates(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!name.trim()) { toast.error("Give the template a name first"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("name", name.trim());
    setUploading(true);
    try {
      await api.post("/increase/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Template uploaded");
      setName(""); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!confirm("Delete this template?")) return;
    try { await api.delete(`/increase/templates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-zinc-200 rounded-sm bg-white mb-4" data-testid="template-manager">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-zinc-50">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-zinc-500" />
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Letter templates</div>
            <div className="text-xs text-zinc-500">
              {templates.length} template{templates.length === 1 ? "" : "s"} — click to {expanded ? "collapse" : "expand"}
            </div>
          </div>
        </div>
        <span className="text-zinc-400 text-xs">{expanded ? "▲" : "▼"}</span>
      </button>
      {expanded && (
        <div className="border-t border-zinc-200 p-4 space-y-4">
          {/* Available tokens palette */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
              Available placeholders — paste any of these into your Word doc as-is:
            </div>
            <div className="flex flex-wrap gap-1">
              {AVAILABLE_TOKENS.map((t) => (
                <button key={t} onClick={() => { navigator.clipboard.writeText(`{{${t}}}`); toast.success(`Copied {{${t}}}`); }}
                  data-testid={`token-${t}`}
                  className="text-[11px] bg-zinc-100 hover:bg-zinc-200 border border-zinc-200 px-2 py-1 rounded-sm font-mono">
                  {`{{${t}}}`}
                </button>
              ))}
            </div>
          </div>
          {/* Uploader */}
          <div className="flex flex-wrap gap-2 items-center bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Template name (e.g. Standard Increase Letter 2026)"
              data-testid="template-name-input"
              className="flex-1 min-w-[240px] border border-zinc-300 px-3 py-1.5 rounded-sm text-sm bg-white" />
            <label className={`flex items-center gap-2 border border-zinc-300 px-3 py-1.5 rounded-sm text-sm bg-white hover:bg-zinc-50 cursor-pointer ${uploading ? "opacity-50" : ""}`}>
              <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload .docx"}
              <input type="file" accept=".docx" className="hidden" onChange={upload} disabled={uploading}
                data-testid="template-file-input" />
            </label>
          </div>
          {/* List */}
          <div>
            {templates.length === 0 ? (
              <div className="text-sm text-zinc-400 text-center py-4">No templates yet.</div>
            ) : (
              <ul className="divide-y divide-zinc-100">
                {templates.map((t) => (
                  <li key={t.id} className="py-2 flex items-center gap-3" data-testid={`template-row-${t.id}`}>
                    <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{t.name}</div>
                      <div className="text-[11px] text-zinc-500">{t.original_filename} · uploaded {(t.created_at || "").split("T")[0]}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {(t.tokens_found || []).map((tok) => (
                          <span key={tok} className="text-[10px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded-sm">
                            {`{{${tok}}}`}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => remove(t.id)} data-testid={`template-delete-${t.id}`}
                      className="text-xs px-2 py-1 border border-zinc-200 rounded-sm hover:bg-red-50 hover:text-red-700 hover:border-red-200 flex items-center gap-1">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function CycleOpener({ onCancel, onCreate }) {
  const now = new Date();
  const [fy, setFy] = useState(String(now.getFullYear()));
  const [end, setEnd] = useState(`${now.getFullYear()}-12-31`);
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onCancel} data-testid="cycle-opener-modal">
      <div className="bg-white border border-zinc-200 rounded-sm w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-zinc-200 text-sm font-semibold">Open increase cycle</div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Fiscal year</div>
            <input value={fy} onChange={(e) => setFy(e.target.value)}
              data-testid="cycle-fy-input"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" placeholder="2026" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Cycle end (used for pro-rata)</div>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              data-testid="cycle-end-input"
              className="w-full border border-zinc-300 px-3 py-2 rounded-sm text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onCancel} className="px-3 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50">Cancel</button>
            <button data-testid="cycle-create-btn" onClick={() => onCreate({ fiscal_year: fy, cycle_end: end })}
              className="px-4 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800">Create</button>
          </div>
        </div>
      </div>
    </div>
  );
}


function CycleTable({ cycle, onOverride, onApprove, onDelete, onReload }) {
  const [q, setQ] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [sending, setSending] = useState(false);
  const isApproved = cycle.status === "approved";
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  useEffect(() => {
    api.get("/increase/templates").then((r) => {
      setTemplates(r.data);
      if (r.data.length > 0) setTplId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const sendLetters = async () => {
    if (!tplId) return toast.error("Pick a template first");
    if (!confirm(`Send the increase letter to all ${cycle.rows.length} employees now?`)) return;
    setSending(true);
    try {
      const { data } = await api.post(`/increase/cycles/${cycle.id}/send-letters`, { template_id: tplId });
      const ok = data.results.filter((r) => r.status === "sent").length;
      const fail = data.results.length - ok;
      toast.success(`Sent ${ok}/${data.results.length} letters${fail ? ` — ${fail} failed` : ""}`);
      onReload?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const previewLetter = (row) => {
    if (!tplId) return toast.error("Pick a template first");
    window.open(`${backend}/api/increase/cycles/${cycle.id}/letters/${row.user_id}/preview?template_id=${tplId}`, "_blank");
  };

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return cycle.rows;
    return cycle.rows.filter((r) => (r.name || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s));
  }, [cycle.rows, q]);

  const totals = useMemo(() => {
    const cnt = cycle.rows.length;
    const withPct = cycle.rows.filter((r) => r.pct_final !== null && r.pct_final !== undefined);
    const avgPct = withPct.length ? (withPct.reduce((a, r) => a + r.pct_final, 0) / withPct.length) : null;
    const maxPct = withPct.length ? Math.max(...withPct.map((r) => r.pct_final)) : null;
    return { cnt, avgPct, maxPct };
  }, [cycle.rows]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <MiniStat label="Employees" value={totals.cnt} tone="violet" />
        <MiniStat label="Average increase" value={totals.avgPct !== null ? `${totals.avgPct.toFixed(2)}%` : "—"} tone="emerald" />
        <MiniStat label="Highest" value={totals.maxPct !== null ? `${totals.maxPct}%` : "—"} tone="amber" />
        <MiniStat label="Status" value={cycle.status} tone={isApproved ? "emerald" : "primary"} />
      </div>

      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <input data-testid="increase-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name or email…"
          className="border border-zinc-300 px-3 py-1.5 rounded-sm text-sm bg-white flex-1 max-w-xs" />
        {templates.length > 0 && (
          <select value={tplId} onChange={(e) => setTplId(e.target.value)}
            data-testid="cycle-template-picker"
            className="border border-zinc-300 px-3 py-1.5 rounded-sm text-sm bg-white">
            {templates.map((t) => <option key={t.id} value={t.id}>Template: {t.name}</option>)}
          </select>
        )}
        <div className="ml-auto flex gap-2">
          {!isApproved && (
            <button data-testid="approve-cycle-btn" onClick={onApprove}
              className="text-sm bg-emerald-600 text-white px-4 py-1.5 rounded-sm hover:bg-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4" /> Approve cycle
            </button>
          )}
          {isApproved && (
            <>
              <span className="text-sm inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-sm border border-emerald-200">
                <Lock className="w-4 h-4" /> Approved
              </span>
              {templates.length > 0 ? (
                <button data-testid="send-letters-btn" onClick={sendLetters} disabled={sending}
                  className="text-sm bg-sky-600 text-white px-4 py-1.5 rounded-sm hover:bg-sky-700 disabled:opacity-50 flex items-center gap-1">
                  <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send letters"}
                </button>
              ) : (
                <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-sm">
                  Upload a template above to send letters
                </span>
              )}
            </>
          )}
          <button data-testid="delete-cycle-btn" onClick={onDelete}
            className="text-sm border border-zinc-300 text-zinc-700 px-3 py-1.5 rounded-sm hover:bg-red-50 hover:text-red-700 hover:border-red-300 flex items-center gap-1">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>

      <div className="overflow-auto border border-zinc-200 rounded-sm bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              {["Employee", "Q4 score", "Avg score", "Score used", "Score override", "Auto %", "Pro-rata", "Final %", "% override", "Letter"].map((h) => (
                <th key={h} className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <RowEditor key={r.user_id} row={r} isApproved={isApproved} onOverride={onOverride}
                         canPreview={!!tplId} onPreview={() => previewLetter(r)} />
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={10} className="text-center text-sm text-zinc-400 py-8">No rows.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function RowEditor({ row, isApproved, onOverride, canPreview, onPreview }) {
  const [score, setScore] = useState(row.score_override ?? "");
  const [pct, setPct] = useState(row.pct_override ?? "");
  useEffect(() => { setScore(row.score_override ?? ""); setPct(row.pct_override ?? ""); }, [row]);

  const commit = (field, raw) => {
    const val = raw === "" ? null : Number(raw);
    if (raw !== "" && Number.isNaN(val)) return;
    onOverride(row, { [field]: val });
  };

  const isBad = (v) => v !== null && v !== undefined && (v < 0 || v > 5);

  return (
    <tr className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`increase-row-${row.user_id}`}>
      <td className="px-3 py-2">
        <div className="font-medium">{row.name}</div>
        <div className="text-[11px] text-zinc-500">{row.email}</div>
      </td>
      <td className="px-3 py-2 tabular-nums text-center">{row.q4_score ?? <span className="text-zinc-300">—</span>}</td>
      <td className="px-3 py-2 tabular-nums text-center">{row.average_score ?? <span className="text-zinc-300">—</span>}</td>
      <td className="px-3 py-2 tabular-nums text-center">
        <b>{row.score_used ?? "—"}</b>
      </td>
      <td className="px-3 py-2">
        <input type="number" step="0.1" min="0" max="5"
          disabled={isApproved}
          value={score} onChange={(e) => setScore(e.target.value)}
          onBlur={() => commit("score_override", score)}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          data-testid={`score-override-${row.user_id}`}
          className={`w-16 border rounded-sm px-2 py-1 text-sm bg-white tabular-nums text-center
                      ${isBad(row.score_override) ? "border-red-400" : "border-zinc-300"}
                      disabled:bg-zinc-50 disabled:cursor-not-allowed`} />
      </td>
      <td className="px-3 py-2 tabular-nums text-center text-zinc-600">
        {row.pct_computed !== null && row.pct_computed !== undefined ? `${row.pct_computed}%` : "—"}
      </td>
      <td className="px-3 py-2 tabular-nums text-center text-zinc-600">
        {row.pro_rata_factor !== 1 ? `×${row.pro_rata_factor}` : <span className="text-zinc-300">—</span>}
      </td>
      <td className="px-3 py-2 tabular-nums text-center">
        <b className="text-emerald-700">{row.pct_final !== null && row.pct_final !== undefined ? `${row.pct_final}%` : "—"}</b>
      </td>
      <td className="px-3 py-2">
        <input type="number" step="0.1" min="0" max="100"
          disabled={isApproved}
          value={pct} onChange={(e) => setPct(e.target.value)}
          onBlur={() => commit("pct_override", pct)}
          onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
          data-testid={`pct-override-${row.user_id}`}
          className="w-20 border border-zinc-300 rounded-sm px-2 py-1 text-sm bg-white tabular-nums text-center disabled:bg-zinc-50 disabled:cursor-not-allowed" />
      </td>
      <td className="px-3 py-2 text-center">
        {canPreview ? (
          <button onClick={onPreview} data-testid={`preview-letter-${row.user_id}`}
            className="text-[11px] inline-flex items-center gap-1 border border-zinc-200 px-2 py-1 rounded-sm hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700">
            <Eye className="w-3 h-3" /> Preview
          </button>
        ) : (
          <span className="text-zinc-300 text-[11px]">—</span>
        )}
      </td>
    </tr>
  );
}


function MiniStat({ label, value, tone = "slate" }) {
  const tones = {
    primary: "border-sky-200 bg-sky-50 text-sky-900",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    violet: "border-violet-200 bg-violet-50 text-violet-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    slate: "border-zinc-200 bg-white text-zinc-900",
  };
  return (
    <div className={`border p-3 rounded-sm ${tones[tone] || tones.slate}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
      <div className="text-2xl font-semibold tabular-nums mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
    </div>
  );
}
